import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  DownloadIcon,
  CloseIcon,
  SearchIcon,
  PlusIcon,
  ChevronDownIcon,
  UsersIcon,
} from "../Icons";
import AddEmployeeModal from "../components/Employees/AddEmployeeModal";
import Table from "../components/UI/Table";
import { getEmployeeColumns } from "../utils/employeeColumns";
import { useEmployees } from "../hooks/useEmployees";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
  PAGE_ACTIONS,
  MODAL_BACKDROP,
  MODAL_PANEL,
  SPINNER,
} from "../ui/pageChrome";
import {
  EMPLOYEE_THEME,
  EMPLOYEE_ROLE_CHIP,
} from "../components/Employees/employeeTheme";

/** Role filter options (patient-style dropdown, not icon tabs). */
const ROLE_OPTIONS = [
  { value: "All", label: "All Roles" },
  { value: "RN", label: "RN" },
  { value: "PT", label: "PT" },
  { value: "PTA", label: "PTA" },
  { value: "OT", label: "OT" },
  { value: "OTA", label: "OTA" },
  { value: "ST", label: "ST" },
  { value: "HHA", label: "HHA" },
  { value: "LVN", label: "LVN" },
  { value: "MSW", label: "MSW" },
  { value: "CG", label: "CG" },
  { value: "CNA", label: "CNA" },
  { value: "ADMIN", label: "ADMIN" },
  { value: "OA", label: "OA" },
  { value: "SRVY", label: "SRVY" },
  { value: "CON", label: "CON" },
];

const ROLE_TAG_COLORS = Object.fromEntries(
  ROLE_OPTIONS.map((opt) => [opt.value, EMPLOYEE_ROLE_CHIP]),
);

const Employees = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPosition = useMemo(() => {
    return searchParams.get("position") || location.state?.filterPosition || "All";
  }, [searchParams, location.state?.filterPosition]);

  const {
    users: displayedUsers,
    loading,
    error,
    pagination,
    filters,
    editing,
    setEditing,
    setFilters,
    setPagination,
    loadUsers: refreshUsers,
    handleCellChange,
    toggleSort,
    exportData,
  } = useEmployees({ position: initialPosition });

  useEffect(() => {
    const position = searchParams.get("position") || "All";
    if (position !== filters.position) {
      setFilters((prev) => ({ ...prev, position }));
    }
  }, [searchParams, filters.position, setFilters]);

  const [addUserModal, setAddUserModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [exportFilter, setExportFilter] = useState({ type: "all", value: "" });
  const [search, setSearch] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [exporting, setExporting] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const isAdmin = currentUser.roleId === 1;

  const filterPosition = filters.position || "All";
  const hasActiveFilters = Boolean(search.trim()) || filterPosition !== "All";

  const setFilterPosition = (newPos) => {
    setFilters((prev) => ({ ...prev, position: newPos }));
    setPagination((p) => ({ ...p, currentPage: 1 }));
    setSearchParams((prev) => {
      if (newPos === "All") prev.delete("position");
      else prev.set("position", newPos);
      return prev;
    });
  };

  const clearFilters = () => {
    setSearch("");
    setFilterPosition("All");
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search }));
    }, 500);
    return () => clearTimeout(timer);
  }, [search, setFilters]);

  const columns = useMemo(
    () =>
      getEmployeeColumns({
        tagColors: ROLE_TAG_COLORS,
        editing,
        setEditing,
        handleCellChange,
        setUsers: () => {},
        navigate,
        isAdmin,
      }),
    [editing, navigate, handleCellChange, setEditing, isAdmin],
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await exportData(exportFilter, search, filterPosition);
      if (result.success) {
        setExportModal(false);
      } else {
        setAlertMessage("Export failed: " + result.error);
        setShowAlert(true);
      }
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${EMPLOYEE_THEME.section.title}`}>
            Employees
          </h1>
          <p className={PAGE_SUBTITLE}>
            Manage staff records, roles, and credentials
          </p>
        </div>
        <div className={PAGE_ACTIONS}>
          <button
            type="button"
            onClick={() => {
              setExportFilter({ type: "all", value: "" });
              setExportModal(true);
            }}
            className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md transition-colors disabled:opacity-50 border ${EMPLOYEE_THEME.button.outline}`}
          >
            <DownloadIcon size={15} />
            Export CSV
          </button>
          <button
            type="button"
            onClick={() =>
              navigate("/employees/add", {
                state: { position: filterPosition },
              })
            }
            className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors ${EMPLOYEE_THEME.button.primary}`}
          >
            <PlusIcon size={16} />
            Add Employee
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
          {error}
        </div>
      ) : null}

      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden ${EMPLOYEE_THEME.section.wrap}`}
      >
        <div
          className={`flex items-center gap-2 h-11 px-4 border-b box-border ${EMPLOYEE_THEME.section.head}`}
        >
          <span
            className={`w-0.5 h-4 rounded-full shrink-0 ${EMPLOYEE_THEME.section.bar}`}
            aria-hidden
          />
          <span className={`shrink-0 ${EMPLOYEE_THEME.section.icon}`}>
            <UsersIcon size={14} />
          </span>
          <h2
            className={`min-w-0 flex-1 text-xs font-semibold uppercase tracking-wide truncate ${EMPLOYEE_THEME.section.title}`}
          >
            Employee List
          </h2>
          {typeof pagination.total === "number" ? (
            <span
              className={`inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold tabular-nums shrink-0 ${EMPLOYEE_THEME.section.badge}`}
            >
              {pagination.total}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search employees..."
              className={`w-full h-8 pl-9 pr-8 text-sm bg-white border border-indigo-200 rounded-md text-slate-800 placeholder:text-slate-400 outline-none transition-colors ${EMPLOYEE_THEME.fieldFocus}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="relative">
              <label className="sr-only" htmlFor="employee-role-filter">
                Filter by role
              </label>
              <select
                id="employee-role-filter"
                className={`appearance-none h-8 min-w-[150px] pl-3 pr-8 text-sm rounded-md border outline-none transition-colors cursor-pointer bg-white ${EMPLOYEE_THEME.fieldFocus} ${
                  filterPosition !== "All"
                    ? "border-indigo-400 text-indigo-700 font-medium bg-indigo-50/60"
                    : "border-indigo-200 text-slate-700"
                }`}
                value={filterPosition}
                onChange={(e) => setFilterPosition(e.target.value)}
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className="h-8 px-2.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 rounded-md border border-indigo-200 transition-colors whitespace-nowrap"
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>

        <Table
          columns={columns}
          data={displayedUsers}
          keyField="id"
          sortKey={filters.sortKey}
          sortOrder={filters.sortOrder}
          onSort={toggleSort}
          loading={loading}
          density="compact"
          embedded
          headTone={EMPLOYEE_THEME.tableHead}
          pagination={{
            currentPage: pagination.currentPage,
            totalPages: pagination.totalPages,
            totalItems: pagination.total,
            itemsPerPage: pagination.limit,
            onPageChange: (page) =>
              setPagination((p) => ({ ...p, currentPage: page })),
          }}
          noDataMessage="No employees found."
        />
      </div>

      {addUserModal && (
        <AddEmployeeModal
          onClose={() => setAddUserModal(false)}
          onSuccess={() => {
            setAddUserModal(false);
            refreshUsers();
          }}
          defaultRoleName={filterPosition}
        />
      )}

      {exportModal && (
        <div className={MODAL_BACKDROP}>
          <div className={MODAL_PANEL} role="dialog" aria-modal="true">
            <div
              className={`flex items-center justify-between gap-3 px-4 py-3 border-b ${EMPLOYEE_THEME.modalHead}`}
            >
              <h2 className={`text-sm font-semibold ${EMPLOYEE_THEME.modalTitle}`}>
                Export Employees
              </h2>
              <button
                type="button"
                onClick={() => setExportModal(false)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-indigo-50"
                aria-label="Close"
              >
                <CloseIcon size={14} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-slate-600">
                Download the current employee list as CSV?
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setExportModal(false)}
                  className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border transition-colors ${EMPLOYEE_THEME.button.outline}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={exporting}
                  className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 ${EMPLOYEE_THEME.button.primary}`}
                >
                  {exporting ? <span className={SPINNER} /> : null}
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAlert && (
        <div className={MODAL_BACKDROP}>
          <div className={MODAL_PANEL} role="dialog" aria-modal="true">
            <div
              className={`flex items-center justify-between gap-3 px-4 py-3 border-b ${EMPLOYEE_THEME.modalHead}`}
            >
              <h2 className={`text-sm font-semibold ${EMPLOYEE_THEME.modalTitle}`}>
                Notice
              </h2>
              <button
                type="button"
                onClick={() => setShowAlert(false)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-indigo-50"
                aria-label="Close"
              >
                <CloseIcon size={14} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-slate-700">{alertMessage}</p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAlert(false)}
                  className={`inline-flex items-center justify-center gap-1.5 h-9 px-3 text-sm font-semibold rounded-md transition-colors ${EMPLOYEE_THEME.button.primary}`}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
